import { hueFor, initials } from './avatar';
import type { InboxTriageCardData, InboxTriageRow } from './types';
import '@/features/briefing/BriefingCard.css';

/**
 * Inbox triage — "email stack + discussion". The agent's short text reply is
 * the discussion; this card is the curated stack: people who need a reply,
 * then the notifications that matter (deliveries, security, money), with the
 * filtered noise folded into a count. Shares the briefing card's visual
 * language (bc-* tokens, blue family, stacked rows).
 */

const KIND_META: Record<InboxTriageRow['kind'], { icon: string; label: string }> = {
  person: { icon: '✉️', label: 'reply' },
  delivery: { icon: '📦', label: 'delivery' },
  security: { icon: '🛡️', label: 'security' },
  finance: { icon: '💳', label: 'finance' },
  event: { icon: '📅', label: 'event' },
  notice: { icon: '🔔', label: 'notice' },
};

function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

function Row({ row, onOpen }: { row: InboxTriageRow; onOpen: (row: InboxTriageRow) => void }) {
  const meta = KIND_META[row.kind] ?? KIND_META.notice;
  return (
    <button type="button" className="bc-mail-row" onClick={() => onOpen(row)}>
      <span className="bc-mail-avatar" style={{ background: hueFor(row.senderEmail || row.senderName || '?') }}>
        {row.kind === 'person' ? initials(row.senderName, row.senderEmail) : meta.icon}
      </span>
      <span className="bc-mail-main">
        <span className="bc-mail-top">
          <span className={`bc-mail-sender${row.isUnread ? ' is-unread' : ''}`}>{row.senderName}</span>
          <span className="bc-mail-time">{timeLabel(row.date)}</span>
        </span>
        <span className="bc-mail-subject">{row.subject}</span>
        {row.kind === 'person' && row.snippet && <span className="bc-mail-snippet">{row.snippet}</span>}
      </span>
    </button>
  );
}

export function InboxTriageCard({
  data,
  onOpenEmail,
  onOpenInbox,
}: {
  data: InboxTriageCardData;
  onOpenEmail: (row: InboxTriageRow) => void;
  onOpenInbox: () => void;
}) {
  const people = data.people ?? [];
  const notifications = data.notifications ?? [];
  const hidden = (data.hiddenCount ?? 0) + (data.duplicates ?? 0);

  return (
    <div className="bc-card">
      {people.length > 0 && (
        <>
          <div className="bc-header">
            <span>Needs you · {people.length}</span>
          </div>
          <div className="bc-mail-stack">
            {people.map((r) => (
              <Row key={r.messageId} row={r} onOpen={onOpenEmail} />
            ))}
          </div>
        </>
      )}

      {notifications.length > 0 && (
        <div className={people.length > 0 ? 'bc-section' : undefined}>
          <div className="bc-header">
            <span>Worth knowing · {notifications.length}</span>
          </div>
          <div className="bc-mail-stack">
            {notifications.map((r) => (
              <Row key={r.messageId} row={r} onOpen={onOpenEmail} />
            ))}
          </div>
        </div>
      )}

      {people.length === 0 && notifications.length === 0 && (
        <div className="bc-empty-day">Inbox clear — nothing needs you.</div>
      )}

      <button type="button" className="bc-link bc-link-cal" onClick={onOpenInbox}>
        {hidden > 0 ? `${hidden} routine email${hidden === 1 ? '' : 's'} filtered · ` : ''}Open inbox{' '}
        <span aria-hidden>→</span>
      </button>
    </div>
  );
}

export default InboxTriageCard;
